/**
 * Advertising results.
 *
 * "Uday and his login can we have a part of the software that shows all ads
 *  Google / FB — and when a client comes in it separates where it came from …
 *  the idea is to show Month of Sept, 10 leads from Google iv 6 converted,
 *  dates times all ways to track what comes in to the website"
 *
 * The page leads with the sentence she asked for, in words, because "which ad
 * should I keep paying for" is the question and a grid of counts makes you work
 * it out yourself. The tables underneath are the same renderer as Reports.
 *
 * It is also the whole of Uday's software: given the Advertising section and
 * nothing else, this is the only page he can open, and the server leaves the
 * names and the money out of what it sends him.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Megaphone, RefreshCw, Download, Link2, Check, TrendingUp } from "lucide-react";
import { Button, EmptyState, Input, Skeleton, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  DateRangePicker, PRESETS, ReportTable, formatCell, type ReportDoc,
} from "@/features/reports/ReportView";

interface Payload {
  report: ReportDoc;
  showing: { names: boolean; money: boolean };
}

export function AdsPage() {
  const toast = useToast();
  /**
   * "And she is not on the advertising list"
   *
   * She was, in October. The page opened on "Month to date", and she looked
   * on the 2nd of November - a two-day window that could not contain an
   * enquiry from the 31st. The first days of every month made the page look
   * broken. Last 30 days always holds the enquiry she is thinking of.
   */
  const [preset, setPreset] = useState("last_30");
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null);
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    const params = custom ? { from: custom.from, to: custom.to } : { preset };
    api.get<Payload>("/ads/where-from", params)
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => setErr(e instanceof ApiError ? e.message : "That didn't load"))
      .finally(() => setLoading(false));
  }, [preset, custom]);

  useEffect(() => { load(); }, [load]);

  const doc = data?.report;
  const sources = doc?.sections.find((s) => s.key === "sources");
  const months = doc?.sections.find((s) => s.key === "months");
  const leads = doc?.sections.find((s) => s.key === "leads");

  /** How many arrived on a properly tagged ad link. Zero means the page is blind. */
  const tagged = useMemo(() => {
    const m = /(\d+) arrived on a tagged ad link/.exec(doc?.subtitle ?? "");
    return m ? Number(m[1]) : null;
  }, [doc?.subtitle]);

  const exportCsv = () => {
    if (!doc) return;
    const lines: string[] = [];
    for (const sec of doc.sections) {
      lines.push(sec.title ?? sec.key);
      lines.push(sec.columns.map((c) => `"${c.label}"`).join(","));
      for (const row of sec.rows) {
        lines.push(sec.columns.map((c) => `"${formatCell(row[c.key], c.type).replace(/"/g, '""')}"`).join(","));
      }
      lines.push("");
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `where-clients-come-from-${doc.range.from}-to-${doc.range.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Downloaded");
  };

  return (
    <div data-ads-page className="space-y-5 p-4 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-xl font-semibold text-zinc-100">
            <Megaphone className="h-5 w-5 text-fuchsia-400" />
            Where clients come from
          </h1>
          <p className="mt-1 text-sm text-zinc-400">
            Every enquiry, which ad or campaign brought it, and how many of them turned up.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker
            preset={preset}
            custom={custom}
            label={doc?.range.label ?? PRESETS.find((p) => p.key === preset)?.label ?? ""}
            onPreset={(p) => { setCustom(null); setPreset(p); }}
            onCustom={(from, to) => setCustom({ from, to })}
          />
          <Button variant="secondary" size="sm" onClick={load} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>
            Refresh
          </Button>
          <Button variant="secondary" size="sm" onClick={exportCsv} disabled={!doc} leftIcon={<Download className="h-3.5 w-3.5" />}>
            Export
          </Button>
        </div>
      </div>

      {/* The answer, in a sentence, before any table. */}
      {loading && !doc ? <Skeleton className="h-20" /> : err ? (
        <EmptyState icon={Megaphone} title="That didn't load" description={err} />
      ) : doc && (
        <>
          <div className="rounded-xl border border-fuchsia-500/25 bg-fuchsia-500/5 px-4 py-3">
            <p className="flex items-start gap-2 text-sm leading-relaxed text-zinc-200">
              <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-fuchsia-400" />
              <span>{doc.subtitle}</span>
            </p>
          </div>

          <TagYourAds hasTagged={(tagged ?? 0) > 0} />

          {sources && (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-zinc-200">{sources.title}</h2>
              <ReportTable section={sources} filter="" />
            </section>
          )}

          {months && months.rows.length > 1 && (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-zinc-200">{months.title}</h2>
              <ReportTable section={months} filter="" />
            </section>
          )}

          {leads && (
            <section className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-zinc-200">{leads.title}</h2>
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Find a source, campaign or name"
                  className="h-8 w-64"
                />
              </div>
              <ReportTable section={leads} filter={filter} />
              {data && !data.showing.names && (
                <p className="text-[11px] text-zinc-500">
                  Client names and phone numbers are not shown on this login.
                </p>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Nothing tagged means nothing to report, so this says what to do about it
 * rather than leaving an empty table to be puzzled over.
 *
 * Her marketing website is not hosted here, so the beacon that records a
 * campaign only runs once somebody reaches the booking page. The link has to
 * carry the tag that far.
 */
/**
 * The links to put in the ads.
 *
 * "I want him to set up the ads as Google IV / Google weight loss / Google NAD
 *  … This way they are separated in the advertising and we follow them as when
 *  they come in from where and if they converted."
 *
 * So the page hands those out ready-made rather than explaining a format: one
 * per campaign, named the way she named them, to be copied into the ad. The
 * tag in the link is what separates the lines on this page, and it also lands
 * the click on the right treatment instead of asking what they came for.
 */
const CAMPAIGNS: { campaign: string; label: string }[] = [
  { campaign: "iv", label: "IV" },
  { campaign: "nad", label: "NAD" },
  { campaign: "weight-loss", label: "Weight loss" },
  { campaign: "injections", label: "Wellness injections" },
  { campaign: "peptides", label: "Peptides" },
  { campaign: "botox", label: "Botox and fillers" },
];
const SOURCES: { source: string; label: string; medium: string }[] = [
  { source: "google", label: "Google", medium: "cpc" },
  { source: "facebook", label: "Facebook", medium: "paid_social" },
  { source: "instagram", label: "Instagram", medium: "paid_social" },
];

function adLink(source: string, medium: string, campaign: string): string {
  return `https://app.heavenlyhydrationli.com/booking`
    + `?utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign}`;
}

function TagYourAds({ hasTagged }: { hasTagged: boolean }) {
  const [source, setSource] = useState(SOURCES[0]!);
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (campaign: string) => {
    try {
      await navigator.clipboard.writeText(adLink(source.source, source.medium, campaign));
      setCopied(campaign);
      setTimeout(() => setCopied(null), 1800);
    } catch { /* clipboard blocked; the link is on screen */ }
  };

  return (
    <div className={cn(
      "space-y-3 rounded-xl border px-4 py-3",
      hasTagged ? "border-zinc-800 bg-zinc-900/30" : "border-amber-500/30 bg-amber-500/5",
    )}>
      {!hasTagged && (
        <p className="text-sm font-medium text-amber-200">
          No ads are tagged yet, so this page cannot tell Google from anything else.
        </p>
      )}
      <p className="text-xs leading-relaxed text-zinc-300">
        Give each ad its own link. Whoever clicks it shows up on this page under that name
        - "Google · weight-loss" - and lands straight on that treatment instead of being
        asked what they came for.
      </p>

      <div className="flex flex-wrap gap-1.5">
        {SOURCES.map((sv) => (
          <button
            key={sv.source}
            type="button"
            onClick={() => setSource(sv)}
            className={cn(
              "rounded-lg border px-2.5 py-1 text-xs font-medium",
              source.source === sv.source
                ? "border-fuchsia-500/60 bg-fuchsia-500/10 text-zinc-100"
                : "border-zinc-800 text-zinc-400 hover:border-zinc-700",
            )}
          >
            {sv.label}
          </button>
        ))}
      </div>

      {/* On a phone the link used to squeeze down to a single letter between
          the name and the Copy button, which reads as broken. The name and the
          link sit on their own lines until there is room for both. */}
      <ul className="divide-y divide-zinc-800/60 overflow-hidden rounded-lg border border-zinc-800">
        {CAMPAIGNS.map((c) => (
          <li key={c.campaign} className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-center sm:gap-3">
            <span className="text-sm text-zinc-200 sm:w-40 sm:shrink-0">
              {source.label} {c.label}
            </span>
            <code className="min-w-0 break-all text-[11px] text-zinc-500 sm:flex-1 sm:truncate sm:break-normal">
              {adLink(source.source, source.medium, c.campaign)}
            </code>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void copy(c.campaign)}
              leftIcon={copied === c.campaign ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
            >
              {copied === c.campaign ? "Copied" : "Copy"}
            </Button>
          </li>
        ))}
      </ul>

      <p className="text-[11px] leading-relaxed text-zinc-500">
        If an ad points at the website instead of straight here, the website has to pass the
        tag through to its Book Now button, or the click arrives with nothing on it. Tell Matt
        which ads go where and he will set that up.
      </p>
    </div>
  );
}
